// ts-rest contract for the firm-wide editable settings (ADR-0006).
//
// The legacy Ops Settings UI used the generic `/api/db/:table` CRUD path
// against `settings_kv` to read tolerances and notification toggles. With
// `firm_config` as a typed singleton row, the generic per-session CRUD no
// longer fits: there is one row, it is not session-scoped, and adding a
// new field is a column migration rather than a key insert. This contract
// is the typed read / patch surface the Ops Settings page consumes.
//
// Endpoints under /api/firm-config:
//   GET   /api/firm-config — read the singleton row (returns all-null when
//                            no save has yet happened)
//   PATCH /api/firm-config — patch a subset of fields, bump updated_at
//
// NOTE (PRD #260 / ADR-0017): the notification-toggle columns that previously
// lived on firm_config (`emailCritical`, `emailAttention`, `emailInfo`,
// `slackEnabled`, `dailyDigest`, `weeklyReport`) were the wrong tier and have
// been dropped (migration 0041). Per-user notification preferences are now
// served by `/api/user-prefs/notifications` (see `user-prefs.ts`).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionOkSchema, staffActionErrorSchema } from './agent';

const c = initContract();

// -----------------------------------------------------------------------
// Settings save + User Management endpoints (OLD_CODE.html #2 replacement
// for the `save-tolerances` / `save-notifications` / `add-user` /
// `deactivate-user` named actions in the old registry).
// -----------------------------------------------------------------------

export const tolerancesPatchSchema = z.object({
    positionTolerance: z.string().optional(),
    cashTolerance: z.string().optional(),
    escalationHours: z.string().optional(),
    minCashThreshold: z.string().optional(),
    excessCashThreshold: z.string().optional(),
    settlementAlertDays: z.string().optional(),
});
export type TolerancesPatchDto = z.infer<typeof tolerancesPatchSchema>;

/**
 * @deprecated PRD #260 / ADR-0017 — firm-global notification toggles were
 * the wrong tier. Replaced by per-user `user_notification_prefs` and the
 * `/api/user-prefs/notifications` endpoint. This schema is kept as an empty
 * object so the saveNotifications endpoint can accept (and no-op) an empty
 * body without a type error; the route will be removed once all callers
 * have migrated.
 */
export const notificationsPatchSchema = z.object({});
export type NotificationsPatchDto = z.infer<typeof notificationsPatchSchema>;

export const addUserRequestSchema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
    role: z.string().optional(),
    personas: z.array(z.string()).min(1),
});
export type AddUserRequest = z.infer<typeof addUserRequestSchema>;

export const updateUserRequestSchema = z.object({
    personas: z.array(z.string()).min(1).optional(),
    role: z.string().optional(),
});
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

/**
 * Typed firm-wide settings row. Mirrors the server-side `FirmConfig`
 * shape; every tolerance field is nullable so a freshly-bootstrapped row
 * (all columns null) round-trips.
 *
 * NOTE: notification columns have been removed (migration 0041 / PRD #260).
 */
export const firmConfigSchema = z.object({
    // --- Ops tolerances ---
    positionTolerance: z.string().nullable(),
    cashTolerance: z.string().nullable(),
    escalationHours: z.string().nullable(),
    minCashThreshold: z.string().nullable(),
    excessCashThreshold: z.string().nullable(),
    settlementAlertDays: z.string().nullable(),

    /** ISO 8601 — bumps on every write. */
    updatedAt: z.string(),
});
export type FirmConfigDto = z.infer<typeof firmConfigSchema>;

/**
 * Patch shape — any subset of the firm-config fields. Server-side, fields
 * absent from the patch are left untouched; explicit `null` clears the
 * column.
 */
export const firmConfigPatchSchema = firmConfigSchema.omit({ updatedAt: true }).partial();
export type FirmConfigPatchDto = z.infer<typeof firmConfigPatchSchema>;

/**
 * The tolerance subset of the firm-config row — the values the View →
 * Administration Operational Tolerances card reads. Carried on its own
 * admin-gated read (`getTolerances`) so a non-admin cannot fetch the persisted
 * tolerance values; the shared `get` stays `ops`. ADR-0013, issue #209.
 */
export const tolerancesSchema = firmConfigSchema.pick({
    positionTolerance: true,
    cashTolerance: true,
    escalationHours: true,
    minCashThreshold: true,
    excessCashThreshold: true,
    settlementAlertDays: true,
    updatedAt: true,
});
export type TolerancesDto = z.infer<typeof tolerancesSchema>;

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

export const firmConfigContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/firm-config',
            responses: {
                200: firmConfigSchema,
            },
            summary: 'Read the firm-wide settings singleton row',
        },
        patch: {
            method: 'PATCH',
            path: '/firm-config',
            body: firmConfigPatchSchema,
            responses: {
                200: firmConfigSchema,
                400: errorBody,
            },
            summary: 'Patch a subset of firm-wide settings fields',
        },
        getTolerances: {
            method: 'GET',
            path: '/firm-config/tolerances',
            responses: {
                200: tolerancesSchema,
            },
            summary:
                'Read the Ops tolerance values. Persona-gated to admin (ADR-0013, issue #209) — a non-admin cannot fetch them.',
        },
        saveTolerances: {
            method: 'PATCH',
            path: '/firm-config/tolerances',
            body: tolerancesPatchSchema,
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
            },
            summary: 'Save the Ops tolerances. Persona-gated to ops/admin; audit-stamped.',
        },
        saveNotifications: {
            method: 'PATCH',
            path: '/firm-config/notifications',
            body: notificationsPatchSchema,
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
            },
            summary:
                '@deprecated (PRD #260) — firm-global notification toggles have been removed. Use /api/user-prefs/notifications instead.',
        },
        addUser: {
            method: 'POST',
            path: '/firm-config/users',
            body: addUserRequestSchema,
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
            },
            summary:
                'Add a user to the firm-global Entra allowlist (ADR-0004). Persona-gated to admin only.',
        },
        updateUser: {
            method: 'PATCH',
            path: '/firm-config/users/:id',
            pathParams: z.object({ id: z.string() }),
            body: updateUserRequestSchema,
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary:
                "Update a user's personas / SMCR role. Persona-gated to admin only (ADR-0004).",
        },
        deactivateUser: {
            method: 'POST',
            path: '/firm-config/users/:id/deactivate',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Deactivate a user (blocks future Entra logins). Persona-gated to admin only.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
